import IParser from "@/types/IParser"
import ParseError from "@/types/ParseError"
import { Bill } from "./bill"
import { BillStatus, IBill } from "./bill.types"
import { safeDayjs } from "@/helpers/dateHelper"
import { startCase } from "lodash-es"
import {
  AccountBill,
  AccountBillStatus,
} from "@/api/backofficeRest/generated/model"

export type IBillRaw = AccountBill

const BILL_STATUS_LABELS: Record<AccountBillStatus, BillStatus> = {
  [AccountBillStatus.ACCEPTED]: "Accepted",
  [AccountBillStatus.DRAFT]: "Draft",
  [AccountBillStatus.ACCEPTANCE_PENDING]: "Acceptance Pending",
}

// A missing or unmapped upstream status must not blank every bill on the
// account, so fall back to a readable label instead of throwing.
const parseBillStatus = (
  status: AccountBillStatus | null | undefined,
): BillStatus => {
  if (!status) {
    return ""
  }
  return (
    BILL_STATUS_LABELS[status] ??
    (startCase(status.toLowerCase()) as BillStatus)
  )
}

export const BillParser: IParser<IBill, IBillRaw> = {
  toDomain: (data: IBillRaw): IBill => {
    try {
      return new Bill(
        Number(data.id),
        parseBillStatus(data.status),
        data.grossAmount,
        data.balance,
        data.billNumber,
        data.superseded,
        data.dirty ?? false,
        data.final,
        data.consolidatedBillFl,
        !!data.deleted,
        safeDayjs(data.billAcceptedAt),
        safeDayjs(data.billCreatedAt),
        safeDayjs(data.dueDt),
        safeDayjs(data.fromDttm),
        // toDttm is an exclusive end (the next period's start); the domain
        // periodTo is the inclusive last day, as Ark's period_to was.
        // Soft-deleting a bill mid-period overwrites toDttm with the deletion
        // timestamp, so the real period end is unrecoverable — leave it unset.
        data.deleted && data.toDttm === data.deleted
          ? undefined
          : safeDayjs(data.toDttm)?.subtract(1, "day"),
      )
    } catch (error) {
      if (error instanceof Error) {
        throw new ParseError(error)
      }

      return null as unknown as IBill
    }
  },
}
